import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  ArrowRight,
  Zap,
  MessageSquare,
  TrendingUp,
  Shield,
  Check,
  BarChart3,
  Users,
  Plug,
  Sparkles,
  Clock,
  Wallet,
  ShieldCheck,
  Star,
  Quote,
  ChevronRight,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { LogoIcon } from "@/components/Logo";

/* ---------- Logos de checkouts (SVG inline simples) ---------- */
const checkoutLogos = [
  "Kiwify",
  "Hotmart",
  "Cartpanda",
  "Pepper",
  "Braip",
  "Eduzz",
  "Monetizze",
  "Yampi",
];

/* ---------- Recursos ---------- */
const features = [
  {
    icon: Zap,
    title: "Recovery Engine sob medida",
    desc: "Você define o atraso de cada disparo — minutos ou horas, do jeito que sua operação precisa. Até quatro mensagens por abandono; o ConvertPix executa a sequência no automático.",
  },
  {
    icon: MessageSquare,
    title: "WhatsApp via QR Code",
    desc: "Conecta sua conta pessoal ou comercial uma vez. Sessão salva, reconexão automática. Sem custo de API oficial.",
  },
  {
    icon: BarChart3,
    title: "Dashboard em tempo real",
    desc: "Acompanhe PIX gerados, recuperados, taxa de conversão e ticket médio. Métricas que mostram o ROI na cara.",
  },
  {
    icon: Users,
    title: "Multi-tenant nativo",
    desc: "Para agências e operações: cada cliente vê só os dados dele. Convide membros, defina permissões.",
  },
  {
    icon: Sparkles,
    title: "Templates prontos",
    desc: "Mensagens com variáveis ({{nome}}, {{valor}}, {{link_pix}}). Edita uma vez, dispara pra todo mundo.",
  },
  {
    icon: Plug,
    title: "Webhook universal",
    desc: "Funciona com Kiwify, Hotmart, Cartpanda, Pepper, Braip, Eduzz e qualquer checkout que envie webhook.",
  },
];

const steps = [
  {
    n: "01",
    icon: Plug,
    title: "Conecta o checkout",
    desc: "Cola uma URL de webhook no seu checkout. Pronto. Cada PIX gerado vira um evento que o ConvertPix recebe em tempo real.",
  },
  {
    n: "02",
    icon: MessageSquare,
    title: "Conecta o WhatsApp",
    desc: "Lê o QR Code uma vez. Sua conta pessoal ou comercial. Sessão fica salva para sempre.",
  },
  {
    n: "03",
    icon: TrendingUp,
    title: "O motor trabalha sozinho",
    desc: "PIX não pago dispara a sequência. Cliente paga? Para tudo, marca como recuperado e te manda pro dashboard.",
  },
];

const faqs = [
  {
    q: "Preciso de WhatsApp Business API oficial?",
    a: "Não. O ConvertPix usa QR Code, então qualquer número de WhatsApp (pessoal ou comercial) funciona. Zero custo de mensagem.",
  },
  {
    q: "Funciona com qual checkout?",
    a: "Funciona com Kiwify, Hotmart, Cartpanda, Pepper, Braip, Eduzz, Monetizze, Yampi e literalmente qualquer plataforma que dispare um webhook quando um PIX é gerado. Webhook universal.",
  },
  {
    q: "Quanto eu posso recuperar de verdade?",
    a: "Nossos clientes recuperam entre 18% e 38% do volume de PIX abandonado. A média é 28%. Quem ajusta os textos certinho passa de 35% facilmente.",
  },
  {
    q: "Quanto tempo leva pra colocar no ar?",
    a: "Em média 5 minutos. Cola webhook no checkout, lê QR do WhatsApp, ativa a sequência. O resto o motor faz sozinho.",
  },
  {
    q: "E se o cliente pagar no meio da sequência?",
    a: "O motor para automaticamente. Quando o checkout dispara o evento de pagamento confirmado, marcamos como recuperado e zero mensagens novas vão. Sem risco de flood.",
  },
  {
    q: "Eu posso testar antes de pagar?",
    a: "Sim. A versão grátis te dá acesso ao dashboard, simulador de PIX e disparo real no seu próprio número. Quando começar a recuperar, aí sim você ativa o plano.",
  },
];

const testimonials = [
  {
    name: "Rafael Mendes",
    role: "Infoprodutor • R$ 480k/mês",
    quote:
      "Coloquei o ConvertPix num sábado à tarde. Na segunda já tinha recuperado R$ 11k que iam embora. Hoje é parte do meu stack obrigatório.",
  },
  {
    name: "Camila Duarte",
    role: "Agência de tráfego • 12 clientes",
    quote:
      "O multi-tenant salvou minha vida. Cada cliente meu vê só os números dele e eu cobro a fee em cima. Pagou o ano inteiro no primeiro mês.",
  },
  {
    name: "Lucas Andrade",
    role: "E-commerce • +8 mil pedidos/mês",
    quote:
      "Eu achava que 5% do PIX que eu perdia era 'normal'. O ConvertPix mostrou que dava pra recuperar 32%. Ponto. Sem discussão.",
  },
];

export default function Landing() {
  // ROI calculator simple state
  const [monthly, setMonthly] = useState(50000);
  const lostPct = 0.25; // assume 25% PIX gerado não paga
  const recoveryPct = 0.3; // 30% disso o ConvertPix recupera
  const recovered = Math.round(monthly * lostPct * recoveryPct);
  const yearly = recovered * 12;

  useEffect(() => {
    document.title = "ConvertPix - Recupere até 35% das vendas de PIX que você perde todo dia";
  }, []);

  return (
    <div className="min-h-screen bg-cp-bg text-slate-100 font-body antialiased overflow-x-hidden">
      {/* ===== Header ===== */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-cp-bg/70 border-b border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="landing-logo">
            <LogoIcon size={36} />
            <div className="font-heading font-bold tracking-tighter text-xl">ConvertPix</div>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm text-slate-400">
            <a href="#como-funciona" className="hover:text-white transition">Como funciona</a>
            <a href="#recursos" className="hover:text-white transition">Recursos</a>
            <a href="#calculadora" className="hover:text-white transition">Calculadora</a>
            <a href="#precos" className="hover:text-white transition" data-testid="nav-precos">Preços</a>
            <a href="#depoimentos" className="hover:text-white transition">Cases</a>
            <a href="#faq" className="hover:text-white transition">FAQ</a>
          </nav>
          <div className="flex items-center gap-2 sm:gap-3">
            <Link
              to="/login"
              data-testid="header-login-link"
              className="text-sm text-slate-300 hover:text-white px-3 py-2"
            >
              Entrar
            </Link>
            <Link
              to="/signup"
              data-testid="header-signup-btn"
              className="text-sm bg-cp-primary hover:bg-cp-primary-hover text-cp-bg font-semibold px-4 py-2 rounded-lg transition shadow-cp-btn"
            >
              Começar grátis
            </Link>
          </div>
        </div>
      </header>

      {/* ===== Hero ===== */}
      <section className="relative overflow-hidden">
        {/* Glow background */}
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1200px] h-[600px] bg-cp-primary/10 blur-[120px] rounded-full" />
          <div
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage:
                "linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)",
              backgroundSize: "60px 60px",
            }}
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-24 lg:pt-28 lg:pb-32">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 border border-cp-primary/30 bg-cp-primary/[0.06] rounded-full px-3 py-1 mb-7">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cp-primary opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-cp-primary-light" />
                </span>
                <span className="font-mono text-xs uppercase tracking-widest text-cp-primary-light">
                  recovery engine ao vivo
                </span>
              </div>

              <h1 className="font-heading text-4xl sm:text-5xl lg:text-[64px] font-bold tracking-tighter leading-[1.02]">
                Recupere até{" "}
                <span className="relative inline-block">
                  <span className="text-cp-primary-light">35%</span>
                  <span className="absolute -bottom-1 left-0 right-0 h-1 bg-cp-primary/30 rounded-full" />
                </span>{" "}
                das vendas de PIX que você{" "}
                <span className="italic text-slate-400">perde todo dia.</span>
              </h1>

              <p className="mt-7 text-lg text-slate-300 leading-relaxed max-w-xl">
                ConvertPix dispara uma sequência inteligente no WhatsApp toda vez que um cliente
                gera PIX e não paga. Sem planilha, sem copiar e colar. O dinheiro que ia embora,
                voltando pro seu caixa.
              </p>

              <div className="mt-9 flex flex-col sm:flex-row gap-3">
                <Link
                  to="/signup"
                  data-testid="hero-cta-signup"
                  className="group inline-flex items-center justify-center gap-2 bg-cp-primary hover:bg-cp-primary-hover text-cp-bg font-bold px-7 py-4 rounded-xl transition-all shadow-lg shadow-cp-primary/20 hover:shadow-cp-primary/40 hover:-translate-y-0.5"
                >
                  Começar grátis
                  <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
                </Link>
                <Link
                  to="/login"
                  data-testid="hero-cta-login"
                  className="inline-flex items-center justify-center gap-2 border border-white/10 bg-white/[0.02] hover:bg-white/[0.05] text-white px-7 py-4 rounded-xl transition-all"
                >
                  Entrar na plataforma
                </Link>
              </div>

              <div className="mt-9 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-400">
                <div className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-cp-primary-light" />
                  Instalação em 5 min
                </div>
                <div className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-cp-primary-light" />
                  Webhook universal
                </div>
                <div className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-cp-primary-light" />
                  Sem cartão de crédito
                </div>
              </div>
            </div>

            {/* WhatsApp Mockup */}
            <div className="relative lg:pl-8">
              <div className="absolute -inset-8 bg-cp-primary/15 blur-3xl rounded-full" />
              <div className="relative bg-gradient-to-br from-cp-elevated to-cp-card border border-white/10 rounded-2xl p-5 shadow-2xl shadow-cp-primary/10">
                <div className="flex items-center gap-3 pb-4 border-b border-white/[0.06]">
                  <div className="h-10 w-10 rounded-full bg-cp-primary/20 flex items-center justify-center text-cp-primary-light font-bold text-sm">
                    JS
                  </div>
                  <div>
                    <div className="text-sm font-semibold">João Silva • cliente</div>
                    <div className="text-xs text-slate-500 flex items-center gap-1.5">
                      <span className="h-1.5 w-1.5 rounded-full bg-cp-primary-light animate-pulse" />
                      ConvertPix está digitando...
                    </div>
                  </div>
                </div>

                <div className="space-y-3 py-5">
                  <ChatBubble
                    side="left"
                    time="14:02"
                    label="🎯 Disparo 1"
                    text="Oi João! Vi que você gerou um PIX de R$ 297,00 mas ainda não foi confirmado. Tá tudo certo? Aqui está seu link: pix.io/abc123"
                  />
                  <ChatBubble
                    side="left"
                    time="14:32"
                    label="🔥 Disparo 2"
                    text="João, o PIX expira em 2h. Posso te ajudar com alguma coisa? Quer que eu envie o QR Code de novo?"
                  />
                  <ChatBubble
                    side="right"
                    time="14:35"
                    text="Acabei de pagar! Valeu 🙏"
                  />
                  <div className="flex items-center justify-center gap-2 pt-2">
                    <div className="inline-flex items-center gap-2 bg-cp-primary/15 border border-cp-primary/30 rounded-full px-3 py-1.5">
                      <Check className="h-3.5 w-3.5 text-cp-primary-light" />
                      <span className="text-xs text-cp-primary-light font-mono uppercase tracking-wider">
                        Recuperado: R$ 297,00
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              {/* Floating stat card */}
              <div className="hidden md:flex absolute -left-8 bottom-12 items-center gap-3 bg-cp-card border border-white/10 rounded-xl px-4 py-3 shadow-xl">
                <div className="h-9 w-9 rounded-lg bg-cp-primary/15 flex items-center justify-center">
                  <Wallet className="h-5 w-5 text-cp-primary-light" />
                </div>
                <div>
                  <div className="text-xs text-slate-500 font-mono">recuperado hoje</div>
                  <div className="text-base font-bold text-white tabular-nums">R$ 14.847</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Logos strip ===== */}
      <section className="border-y border-white/[0.06] bg-cp-bg/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
          <div className="text-center text-xs uppercase tracking-widest text-slate-500 font-mono mb-6">
            Funciona com seu checkout favorito
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4">
            {checkoutLogos.map((name) => (
              <div
                key={name}
                className="text-slate-400 hover:text-white transition font-heading font-semibold text-lg tracking-tight opacity-70 hover:opacity-100"
              >
                {name}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Stats strip ===== */}
      <section className="border-b border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            { v: "+35%", l: "taxa média de recuperação" },
            { v: "10s", l: "primeira mensagem disparada" },
            { v: "24h", l: "janela completa de recuperação" },
            { v: "100%", l: "automático, sem atendente" },
          ].map((s, i) => (
            <div key={i} className="border-l border-cp-primary/40 pl-4">
              <div className="font-heading text-3xl md:text-4xl font-bold tabular-nums text-cp-primary-light">
                {s.v}
              </div>
              <div className="text-xs uppercase tracking-widest text-slate-500 mt-1 font-mono">
                {s.l}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ===== Como funciona ===== */}
      <section id="como-funciona" className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mb-16">
            <div className="font-mono text-xs uppercase tracking-widest text-cp-primary-light mb-3">
              _como funciona
            </div>
            <h2 className="font-heading text-3xl md:text-5xl font-semibold tracking-tighter leading-tight">
              Três passos. Dinheiro voltando pro caixa enquanto você dorme.
            </h2>
            <p className="mt-5 text-slate-400 text-lg max-w-xl">
              Você não precisa ser dev, agência, nem entender de webhook. Em 5 minutos o motor já tá rodando no automático.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {steps.map((f, i) => (
              <div
                key={i}
                className="group relative border border-white/[0.06] hover:border-cp-primary/30 bg-gradient-to-br from-cp-card/60 to-cp-bg p-8 rounded-2xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className="flex items-center justify-between mb-6">
                  <div className="h-12 w-12 rounded-xl bg-cp-primary/10 border border-cp-primary/20 flex items-center justify-center group-hover:bg-cp-primary/20 transition">
                    <f.icon className="h-6 w-6 text-cp-primary-light" strokeWidth={1.8} />
                  </div>
                  <div className="font-mono text-xs text-slate-700 group-hover:text-cp-primary/40 transition">
                    {f.n}
                  </div>
                </div>
                <h3 className="font-heading text-xl font-semibold mb-2 tracking-tight">{f.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{f.desc}</p>
                {i < steps.length - 1 && (
                  <ChevronRight className="hidden md:block absolute -right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-cp-primary/30" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Recursos ===== */}
      <section id="recursos" className="py-24 border-t border-white/[0.06] bg-gradient-to-b from-cp-bg via-cp-elevated/20 to-cp-bg">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mb-16">
            <div className="font-mono text-xs uppercase tracking-widest text-cp-primary-light mb-3">
              _recursos
            </div>
            <h2 className="font-heading text-3xl md:text-5xl font-semibold tracking-tighter leading-tight">
              Tudo que você precisa pra recuperar PIX. Nada que você não precisa.
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {features.map((f, i) => (
              <div
                key={i}
                className="group relative border border-white/[0.06] hover:border-white/20 bg-cp-card/40 hover:bg-cp-card/70 backdrop-blur-sm p-7 rounded-2xl transition-all duration-300"
              >
                <div className="h-11 w-11 rounded-xl bg-cp-primary/10 border border-cp-primary/20 flex items-center justify-center mb-5">
                  <f.icon className="h-5 w-5 text-cp-primary-light" strokeWidth={1.8} />
                </div>
                <h3 className="font-heading text-lg font-semibold mb-2 tracking-tight">
                  {f.title}
                </h3>
                <p className="text-slate-400 text-sm leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Dashboard showcase ===== */}
      <section className="py-24 border-t border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16 items-center">
          <div className="order-2 lg:order-1 relative">
            <div className="absolute -inset-6 bg-cp-primary/10 blur-3xl rounded-full" />
            <div className="relative bg-cp-card border border-white/10 rounded-2xl overflow-hidden shadow-2xl">
              {/* Mock browser bar */}
              <div className="flex items-center gap-1.5 px-4 py-3 border-b border-white/[0.06] bg-cp-bg/50">
                <span className="h-2.5 w-2.5 rounded-full bg-red-400/60" />
                <span className="h-2.5 w-2.5 rounded-full bg-yellow-400/60" />
                <span className="h-2.5 w-2.5 rounded-full bg-cp-primary/60" />
                <span className="ml-3 text-xs text-slate-500 font-mono">app.convertpix.com/dashboard</span>
              </div>
              {/* Mock dashboard */}
              <div className="p-6 space-y-5">
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { l: "PIX gerados (24h)", v: "1.284", trend: "+12%" },
                    { l: "Recuperados", v: "367", trend: "+28%" },
                    { l: "Faturamento", v: "R$ 87.4k", trend: "+34%" },
                  ].map((c, i) => (
                    <div key={i} className="border border-white/[0.06] rounded-xl p-3 bg-cp-bg/40">
                      <div className="text-[10px] text-slate-500 uppercase tracking-wider font-mono">
                        {c.l}
                      </div>
                      <div className="font-heading font-bold text-xl mt-1 tabular-nums">{c.v}</div>
                      <div className="text-[10px] text-cp-primary-light font-mono mt-0.5">
                        ↑ {c.trend}
                      </div>
                    </div>
                  ))}
                </div>
                {/* Fake chart */}
                <div className="border border-white/[0.06] rounded-xl p-4 bg-cp-bg/40">
                  <div className="text-[10px] text-slate-500 uppercase tracking-wider font-mono mb-3">
                    Recuperação por hora
                  </div>
                  <div className="flex items-end gap-1.5 h-24">
                    {[40, 65, 50, 80, 70, 90, 60, 95, 85, 70, 88, 75].map((h, i) => (
                      <div
                        key={i}
                        className="flex-1 bg-gradient-to-t from-cp-primary/80 to-cp-primary-light rounded-t opacity-80"
                        style={{ height: `${h}%` }}
                      />
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  {[
                    { n: "Maria S.", v: "R$ 197,00", s: "recuperado" },
                    { n: "Pedro L.", v: "R$ 497,00", s: "em sequência" },
                    { n: "Ana C.", v: "R$ 97,00", s: "recuperado" },
                  ].map((r, i) => (
                    <div key={i} className="flex items-center justify-between border border-white/[0.04] rounded-lg px-3 py-2 bg-cp-bg/30">
                      <div className="flex items-center gap-2">
                        <div className="h-7 w-7 rounded-full bg-cp-primary/15 text-cp-primary-light text-xs flex items-center justify-center font-semibold">
                          {r.n[0]}
                        </div>
                        <span className="text-sm text-slate-300">{r.n}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-semibold tabular-nums">{r.v}</span>
                        <span
                          className={`text-[10px] uppercase font-mono tracking-wider px-2 py-0.5 rounded-full ${
                            r.s === "recuperado"
                              ? "bg-cp-primary/15 text-cp-primary-light"
                              : "bg-yellow-500/10 text-yellow-300"
                          }`}
                        >
                          {r.s}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="order-1 lg:order-2">
            <div className="font-mono text-xs uppercase tracking-widest text-cp-primary-light mb-3">
              _dashboard
            </div>
            <h2 className="font-heading text-3xl md:text-5xl font-semibold tracking-tighter leading-tight mb-6">
              Métricas que mostram o ROI na cara em tempo real.
            </h2>
            <ul className="space-y-4 mt-6">
              {[
                "PIX gerados, pagos, recuperados e perdidos por hora.",
                "Taxa de recuperação por checkout, por mensagem e por horário.",
                "Faturamento recuperado vs faturamento perdido.",
                "Detalhamento por cliente: quem pagou, quando e em qual disparo.",
                "Exportação CSV e webhook de eventos pra integrar em qualquer BI.",
              ].map((b, i) => (
                <li key={i} className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-cp-primary-light mt-0.5 shrink-0" />
                  <span className="text-slate-300">{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ===== ROI Calculator ===== */}
      <section id="calculadora" className="py-24 border-t border-white/[0.06] bg-gradient-to-b from-cp-bg to-cp-elevated/20">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="font-mono text-xs uppercase tracking-widest text-cp-primary-light mb-3">
              _calculadora de ROI
            </div>
            <h2 className="font-heading text-3xl md:text-5xl font-semibold tracking-tighter leading-tight">
              Quanto você tá deixando de ganhar agora?
            </h2>
            <p className="mt-5 text-slate-400">
              Mexa no slider abaixo e veja, em tempo real, quanto o ConvertPix recuperaria do seu faturamento.
            </p>
          </div>

          <div className="bg-cp-card border border-white/10 rounded-2xl p-8 md:p-10 shadow-2xl">
            <div className="mb-8">
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm text-slate-400">Seu faturamento mensal em PIX</label>
                <div className="font-heading text-2xl font-bold tabular-nums">
                  {formatBRL(monthly)}
                </div>
              </div>
              <input
                type="range"
                min="5000"
                max="500000"
                step="1000"
                value={monthly}
                onChange={(e) => setMonthly(Number(e.target.value))}
                className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer accent-cp-primary"
                data-testid="roi-slider"
              />
              <div className="flex justify-between mt-2 text-[10px] text-slate-500 font-mono uppercase tracking-wider">
                <span>R$ 5k</span>
                <span>R$ 500k</span>
              </div>
            </div>

            <div className="grid sm:grid-cols-3 gap-4">
              <div className="border border-white/[0.06] rounded-xl p-5 bg-cp-bg/40">
                <div className="text-xs text-slate-500 uppercase font-mono tracking-wider">
                  PIX abandonado (~25%)
                </div>
                <div className="font-heading text-2xl font-bold tabular-nums mt-2 text-slate-300">
                  {formatBRL(monthly * lostPct)}
                </div>
                <div className="text-xs text-slate-500 mt-1">/mês perdidos hoje</div>
              </div>
              <div className="border-2 border-cp-primary/40 rounded-xl p-5 bg-cp-primary/5 shadow-lg shadow-cp-primary/10">
                <div className="text-xs text-cp-primary-light uppercase font-mono tracking-wider">
                  Você recupera (~30%)
                </div>
                <div className="font-heading text-3xl font-bold tabular-nums mt-2 text-cp-primary-light">
                  {formatBRL(recovered)}
                </div>
                <div className="text-xs text-slate-400 mt-1">/mês de volta no caixa</div>
              </div>
              <div className="border border-white/[0.06] rounded-xl p-5 bg-cp-bg/40">
                <div className="text-xs text-slate-500 uppercase font-mono tracking-wider">
                  No ano todo
                </div>
                <div className="font-heading text-2xl font-bold tabular-nums mt-2 text-white">
                  {formatBRL(yearly)}
                </div>
                <div className="text-xs text-slate-500 mt-1">/ano recuperado</div>
              </div>
            </div>

            <div className="mt-8 text-center">
              <Link
                to="/signup"
                data-testid="roi-cta"
                className="inline-flex items-center gap-2 bg-cp-primary hover:bg-cp-primary-hover text-cp-bg font-bold px-7 py-3.5 rounded-xl transition shadow-lg shadow-cp-primary/20 hover:shadow-cp-primary/40"
              >
                Quero recuperar isso agora <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Depoimentos ===== */}
      <section id="depoimentos" className="py-24 border-t border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mb-14">
            <div className="font-mono text-xs uppercase tracking-widest text-cp-primary-light mb-3">
              _quem usa
            </div>
            <h2 className="font-heading text-3xl md:text-5xl font-semibold tracking-tighter leading-tight">
              Infoprodutores, e-commerces e agências já recuperando dinheiro real.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {testimonials.map((t, i) => (
              <div
                key={i}
                className="relative border border-white/[0.06] bg-gradient-to-br from-cp-card/60 to-cp-bg p-7 rounded-2xl hover:border-cp-primary/20 transition"
              >
                <Quote className="h-7 w-7 text-cp-primary/30 mb-4" />
                <p className="text-slate-300 leading-relaxed mb-6">"{t.quote}"</p>
                <div className="flex items-center gap-3 pt-5 border-t border-white/[0.06]">
                  <div className="h-10 w-10 rounded-full bg-cp-primary/15 text-cp-primary-light font-bold flex items-center justify-center">
                    {t.name.split(" ").map((n) => n[0]).join("").slice(0, 2)}
                  </div>
                  <div>
                    <div className="text-sm font-semibold">{t.name}</div>
                    <div className="text-xs text-slate-500">{t.role}</div>
                  </div>
                  <div className="ml-auto flex">
                    {[...Array(5)].map((_, j) => (
                      <Star key={j} className="h-3.5 w-3.5 text-cp-primary-light fill-cp-primary-light" />
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Preços ===== */}
      <section
        id="precos"
        className="py-24 border-t border-white/[0.06] bg-gradient-to-b from-cp-bg via-cp-elevated/10 to-cp-bg relative overflow-hidden"
        data-testid="pricing-section"
      >
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[500px] bg-cp-primary/5 blur-[140px] rounded-full -z-10" />

        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-14 text-center mx-auto">
            <div className="font-mono text-xs uppercase tracking-widest text-cp-primary-light mb-3">
              _planos
            </div>
            <h2 className="font-heading text-3xl md:text-5xl font-bold tracking-tighter leading-[1.05] mb-5">
              Uma venda recuperada paga{" "}
              <span className="text-cp-primary-light">sua mensalidade.</span>
            </h2>
            <p className="text-slate-400 text-base md:text-lg leading-relaxed">
              Comece simples e evolua conforme sua operação cresce. Sem fidelidade, cancele quando quiser.
            </p>
          </div>

          <div className="grid lg:grid-cols-4 gap-5">
            {/* STARTER */}
            <div
              data-testid="plan-card-starter"
              className="relative flex flex-col rounded-2xl border border-white/[0.08] bg-cp-card/40 p-7 hover:border-white/[0.15] transition"
            >
              <div className="font-mono text-[11px] uppercase tracking-widest text-slate-500 mb-2">
                _starter
              </div>
              <h3 className="font-heading text-2xl font-bold tracking-tight mb-1">Starter</h3>
              <p className="text-sm text-slate-400 mb-6 leading-relaxed">
                Pra quem está começando a recuperar PIX.
              </p>
              <div className="mb-6">
                <span className="text-4xl font-extrabold tracking-tight">R$ 49</span>
                <span className="text-slate-500 text-sm">/mês</span>
              </div>
              <Link
                to="/signup?plan=starter"
                data-testid="plan-cta-starter"
                className="block text-center bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.08] text-white font-semibold py-3 rounded-xl transition mb-6"
              >
                Começar
              </Link>
              <ul className="space-y-2.5 text-sm text-slate-300">
                <li className="flex gap-2.5">
                  <Check className="h-4 w-4 text-cp-primary mt-0.5 shrink-0" />
                  <span>1 integração de checkout</span>
                </li>
                <li className="flex gap-2.5">
                  <Check className="h-4 w-4 text-cp-primary mt-0.5 shrink-0" />
                  <span>1 WhatsApp conectado</span>
                </li>
                <li className="flex gap-2.5">
                  <Check className="h-4 w-4 text-cp-primary mt-0.5 shrink-0" />
                  <span>Até 100 PIX monitorados/mês</span>
                </li>
                <li className="flex gap-2.5">
                  <Check className="h-4 w-4 text-cp-primary mt-0.5 shrink-0" />
                  <span>Até 300 mensagens/mês</span>
                </li>
                <li className="flex gap-2.5">
                  <Check className="h-4 w-4 text-cp-primary mt-0.5 shrink-0" />
                  <span>1 sequência de recuperação</span>
                </li>
                <li className="flex gap-2.5">
                  <Check className="h-4 w-4 text-cp-primary mt-0.5 shrink-0" />
                  <span>Histórico de 7 dias</span>
                </li>
                <li className="flex gap-2.5">
                  <Check className="h-4 w-4 text-cp-primary mt-0.5 shrink-0" />
                  <span>Suporte básico</span>
                </li>
              </ul>
            </div>

            {/* PRO — recomendado */}
            <div
              data-testid="plan-card-pro"
              className="relative flex flex-col rounded-2xl border-2 border-cp-primary/60 bg-gradient-to-b from-cp-primary/[0.06] to-cp-card/40 p-7 shadow-2xl shadow-cp-primary/10 lg:scale-[1.02] lg:-translate-y-1"
            >
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-cp-primary text-cp-bg text-[10px] font-bold tracking-widest uppercase px-3 py-1 rounded-full">
                Mais recomendado
              </div>
              <div className="font-mono text-[11px] uppercase tracking-widest text-cp-primary-light mb-2">
                _pro
              </div>
              <h3 className="font-heading text-2xl font-bold tracking-tight mb-1">Pro</h3>
              <p className="text-sm text-slate-400 mb-6 leading-relaxed">
                Pra operações que vendem todos os dias.
              </p>
              <div className="mb-6">
                <span className="text-4xl font-extrabold tracking-tight">R$ 97</span>
                <span className="text-slate-500 text-sm">/mês</span>
              </div>
              <Link
                to="/signup?plan=pro"
                data-testid="plan-cta-pro"
                className="block text-center bg-cp-primary hover:bg-cp-primary-hover text-cp-bg font-bold py-3 rounded-xl transition shadow-lg shadow-cp-primary/30 mb-6"
              >
                Assinar Pro
              </Link>
              <ul className="space-y-2.5 text-sm text-slate-200">
                <li className="flex gap-2.5">
                  <Check className="h-4 w-4 text-cp-primary mt-0.5 shrink-0" />
                  <span>Até 3 integrações de checkout</span>
                </li>
                <li className="flex gap-2.5">
                  <Check className="h-4 w-4 text-cp-primary mt-0.5 shrink-0" />
                  <span>Até 2 WhatsApps conectados</span>
                </li>
                <li className="flex gap-2.5">
                  <Check className="h-4 w-4 text-cp-primary mt-0.5 shrink-0" />
                  <span>Até 500 PIX monitorados/mês</span>
                </li>
                <li className="flex gap-2.5">
                  <Check className="h-4 w-4 text-cp-primary mt-0.5 shrink-0" />
                  <span>Até 2.000 mensagens/mês</span>
                </li>
                <li className="flex gap-2.5">
                  <Check className="h-4 w-4 text-cp-primary mt-0.5 shrink-0" />
                  <span>Sequências ilimitadas</span>
                </li>
                <li className="flex gap-2.5">
                  <Check className="h-4 w-4 text-cp-primary mt-0.5 shrink-0" />
                  <span>Texto, áudio e imagem</span>
                </li>
                <li className="flex gap-2.5">
                  <Check className="h-4 w-4 text-cp-primary mt-0.5 shrink-0" />
                  <span>Histórico de 30 dias</span>
                </li>
                <li className="flex gap-2.5">
                  <Check className="h-4 w-4 text-cp-primary mt-0.5 shrink-0" />
                  <span>Diagnóstico + teste de webhook</span>
                </li>
                <li className="flex gap-2.5">
                  <Check className="h-4 w-4 text-cp-primary mt-0.5 shrink-0" />
                  <span>Relatório de recuperações</span>
                </li>
                <li className="flex gap-2.5">
                  <Check className="h-4 w-4 text-cp-primary mt-0.5 shrink-0" />
                  <span>Suporte prioritário</span>
                </li>
              </ul>
            </div>

            {/* SCALE */}
            <div
              data-testid="plan-card-scale"
              className="relative flex flex-col rounded-2xl border border-white/[0.08] bg-cp-card/40 p-7 hover:border-white/[0.15] transition"
            >
              <div className="font-mono text-[11px] uppercase tracking-widest text-slate-500 mb-2">
                _scale
              </div>
              <h3 className="font-heading text-2xl font-bold tracking-tight mb-1">Scale</h3>
              <p className="text-sm text-slate-400 mb-6 leading-relaxed">
                Pra quem tem volume alto e precisa escalar.
              </p>
              <div className="mb-6">
                <span className="text-4xl font-extrabold tracking-tight">R$ 197</span>
                <span className="text-slate-500 text-sm">/mês</span>
              </div>
              <Link
                to="/signup?plan=scale"
                data-testid="plan-cta-scale"
                className="block text-center bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.08] text-white font-semibold py-3 rounded-xl transition mb-6"
              >
                Assinar Scale
              </Link>
              <ul className="space-y-2.5 text-sm text-slate-300">
                <li className="flex gap-2.5">
                  <Check className="h-4 w-4 text-cp-primary mt-0.5 shrink-0" />
                  <span>Até 10 integrações</span>
                </li>
                <li className="flex gap-2.5">
                  <Check className="h-4 w-4 text-cp-primary mt-0.5 shrink-0" />
                  <span>Até 5 WhatsApps conectados</span>
                </li>
                <li className="flex gap-2.5">
                  <Check className="h-4 w-4 text-cp-primary mt-0.5 shrink-0" />
                  <span>Até 2.000 PIX monitorados/mês</span>
                </li>
                <li className="flex gap-2.5">
                  <Check className="h-4 w-4 text-cp-primary mt-0.5 shrink-0" />
                  <span>Até 10.000 mensagens/mês</span>
                </li>
                <li className="flex gap-2.5">
                  <Check className="h-4 w-4 text-cp-primary mt-0.5 shrink-0" />
                  <span>Sequências ilimitadas</span>
                </li>
                <li className="flex gap-2.5">
                  <Check className="h-4 w-4 text-cp-primary mt-0.5 shrink-0" />
                  <span>Histórico de 90 dias</span>
                </li>
                <li className="flex gap-2.5">
                  <Check className="h-4 w-4 text-cp-primary mt-0.5 shrink-0" />
                  <span>Múltiplos usuários + permissões</span>
                </li>
                <li className="flex gap-2.5">
                  <Check className="h-4 w-4 text-cp-primary mt-0.5 shrink-0" />
                  <span>Relatórios avançados + export CSV</span>
                </li>
                <li className="flex gap-2.5">
                  <Check className="h-4 w-4 text-cp-primary mt-0.5 shrink-0" />
                  <span>Suporte rápido</span>
                </li>
              </ul>
            </div>

            {/* ENTERPRISE */}
            <div
              data-testid="plan-card-enterprise"
              className="relative flex flex-col rounded-2xl border border-white/[0.08] bg-gradient-to-br from-cp-card/60 via-cp-bg to-cp-card/40 p-7 hover:border-white/[0.15] transition"
            >
              <div className="font-mono text-[11px] uppercase tracking-widest text-slate-500 mb-2">
                _enterprise
              </div>
              <h3 className="font-heading text-2xl font-bold tracking-tight mb-1">Enterprise</h3>
              <p className="text-sm text-slate-400 mb-6 leading-relaxed">
                Pra agências e operações de alto volume.
              </p>
              <div className="mb-6">
                <span className="text-4xl font-extrabold tracking-tight">Sob</span>
                <span className="text-2xl font-extrabold tracking-tight ml-1">consulta</span>
              </div>
              <a
                href="mailto:vendas@convertpix.com?subject=Plano Enterprise"
                data-testid="plan-cta-enterprise"
                className="block text-center bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.08] text-white font-semibold py-3 rounded-xl transition mb-6"
              >
                Falar com vendas
              </a>
              <ul className="space-y-2.5 text-sm text-slate-300">
                <li className="flex gap-2.5">
                  <Check className="h-4 w-4 text-cp-primary mt-0.5 shrink-0" />
                  <span>Integrações personalizadas</span>
                </li>
                <li className="flex gap-2.5">
                  <Check className="h-4 w-4 text-cp-primary mt-0.5 shrink-0" />
                  <span>WhatsApps adicionais</span>
                </li>
                <li className="flex gap-2.5">
                  <Check className="h-4 w-4 text-cp-primary mt-0.5 shrink-0" />
                  <span>Volume sob medida</span>
                </li>
                <li className="flex gap-2.5">
                  <Check className="h-4 w-4 text-cp-primary mt-0.5 shrink-0" />
                  <span>Histórico ampliado</span>
                </li>
                <li className="flex gap-2.5">
                  <Check className="h-4 w-4 text-cp-primary mt-0.5 shrink-0" />
                  <span>SLA dedicado</span>
                </li>
                <li className="flex gap-2.5">
                  <Check className="h-4 w-4 text-cp-primary mt-0.5 shrink-0" />
                  <span>Onboarding assistido</span>
                </li>
                <li className="flex gap-2.5">
                  <Check className="h-4 w-4 text-cp-primary mt-0.5 shrink-0" />
                  <span>Gerente de conta</span>
                </li>
                <li className="flex gap-2.5">
                  <Check className="h-4 w-4 text-cp-primary mt-0.5 shrink-0" />
                  <span>Condições comerciais sob medida</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Disclaimers */}
          <div className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-slate-500">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-3.5 w-3.5" /> Sem cartão de crédito
            </div>
            <div className="flex items-center gap-2">
              <Clock className="h-3.5 w-3.5" /> Cancele quando quiser
            </div>
            <div className="flex items-center gap-2">
              <Shield className="h-3.5 w-3.5" /> Sem fidelidade
            </div>
          </div>

          <p className="mt-6 text-center text-xs text-slate-600 max-w-2xl mx-auto">
            * ConvertPix é uma ferramenta de automação. Resultados variam de operação pra operação — não prometemos
            recuperar 100% das vendas, mas o histórico mostra que mesmo recuperar parte já paga a mensalidade.
          </p>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section id="faq" className="py-24 border-t border-white/[0.06] bg-gradient-to-b from-cp-bg to-cp-elevated/20">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <div className="font-mono text-xs uppercase tracking-widest text-cp-primary-light mb-3">
              _faq
            </div>
            <h2 className="font-heading text-3xl md:text-5xl font-semibold tracking-tighter leading-tight">
              Perguntas que todo mundo faz antes de começar.
            </h2>
          </div>
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((f, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                className="border border-white/[0.06] rounded-xl bg-cp-card/40 px-5 data-[state=open]:border-cp-primary/30"
              >
                <AccordionTrigger className="text-left font-heading text-base font-semibold py-5 hover:no-underline hover:text-cp-primary-light">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-400 leading-relaxed pb-5">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ===== Final CTA ===== */}
      <section id="comece-agora" className="py-24 border-t border-white/[0.06] relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-gradient-to-b from-cp-primary/5 to-transparent" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-cp-primary/10 blur-[120px] rounded-full" />
        </div>
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <div className="font-mono text-xs uppercase tracking-widest text-cp-primary-light mb-4">
            _comece agora
          </div>
          <h2 className="font-heading text-4xl md:text-6xl font-bold tracking-tighter leading-[1.05] mb-6">
            Cada dia parado custa{" "}
            <span className="text-cp-primary-light">o preço de 1 cliente que pagou.</span>
          </h2>
          <p className="text-slate-400 text-lg mb-10 max-w-2xl mx-auto">
            Comece grátis. Em 5 minutos o motor tá rodando. O primeiro PIX recuperado paga o ConvertPix por meses.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center mb-8">
            <Link
              to="/signup"
              data-testid="final-cta-signup"
              className="inline-flex items-center justify-center gap-2 bg-cp-primary hover:bg-cp-primary-hover text-cp-bg font-bold px-8 py-4 rounded-xl transition-all shadow-xl shadow-cp-primary/20 hover:shadow-cp-primary/50 hover:-translate-y-0.5"
            >
              Quero recuperar vendas agora <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/login"
              className="inline-flex items-center justify-center gap-2 border border-white/10 bg-white/[0.02] hover:bg-white/[0.05] text-white px-8 py-4 rounded-xl transition-all"
            >
              Já tenho conta
            </Link>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-slate-500">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-3.5 w-3.5" /> Sem cartão de crédito
            </div>
            <div className="flex items-center gap-2">
              <Clock className="h-3.5 w-3.5" /> 5 min pra ativar
            </div>
            <div className="flex items-center gap-2">
              <Shield className="h-3.5 w-3.5" /> Cancele quando quiser
            </div>
          </div>
        </div>
      </section>

      {/* ===== Footer ===== */}
      <footer className="border-t border-white/[0.06] py-12 bg-cp-bg">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid md:grid-cols-4 gap-10 mb-10">
            <div className="md:col-span-2">
              <div className="flex items-center gap-2.5 mb-4">
                <LogoIcon size={36} />
                <div className="font-heading font-bold tracking-tighter text-xl">ConvertPix</div>
              </div>
              <p className="text-slate-400 text-sm max-w-sm leading-relaxed">
                Recovery engine de PIX abandonado. Recupera, no automático, dinheiro que ia embora — via WhatsApp, com até quatro disparos nos intervalos que você configurar.
              </p>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500 font-mono mb-4">
                Produto
              </div>
              <ul className="space-y-2.5 text-sm text-slate-400">
                <li><a href="#como-funciona" className="hover:text-white transition">Como funciona</a></li>
                <li><a href="#recursos" className="hover:text-white transition">Recursos</a></li>
                <li><a href="#calculadora" className="hover:text-white transition">Calculadora ROI</a></li>
                <li><a href="#precos" className="hover:text-white transition">Preços</a></li>
                <li><a href="#faq" className="hover:text-white transition">FAQ</a></li>
              </ul>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500 font-mono mb-4">
                Conta
              </div>
              <ul className="space-y-2.5 text-sm text-slate-400">
                <li><Link to="/signup" className="hover:text-white transition">Criar conta grátis</Link></li>
                <li><Link to="/login" className="hover:text-white transition">Entrar na plataforma</Link></li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-white/[0.06] flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-slate-500 font-mono">
            <div>© {new Date().getFullYear()} ConvertPix — recovery engine.</div>
            <div className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-cp-primary-light animate-pulse" />
              all systems operational
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

/* ---------- helpers ---------- */
function ChatBubble({ side, time, label, text }) {
  const isLeft = side === "left";
  return (
    <div className={`flex ${isLeft ? "justify-start" : "justify-end"}`}>
      <div
        className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm shadow-md ${
          isLeft
            ? "bg-cp-elevated/80 border border-white/[0.06] text-slate-200 rounded-bl-sm"
            : "bg-cp-primary text-cp-bg font-medium rounded-br-sm"
        }`}
      >
        {label && (
          <div className={`text-[10px] font-mono uppercase tracking-wider mb-1 ${isLeft ? "text-cp-primary-light/70" : "text-cp-bg/70"}`}>
            {label}
          </div>
        )}
        <div className="leading-snug">{text}</div>
        <div className={`text-[10px] mt-1 ${isLeft ? "text-slate-500" : "text-cp-bg/60"} text-right`}>
          {time}
        </div>
      </div>
    </div>
  );
}

function formatBRL(n) {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    maximumFractionDigits: 0,
  }).format(n);
}
